import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, Upload, FileText, KeyRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useRef } from "react";
import { API_BASE } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import PasswordInput from "@/components/PasswordInput";

export default function Admin() {
  const [tab, setTab] = useState("users");
  const tabs = [
    { id: "users", label: "Users" },
    { id: "customers", label: "Customers" },
    { id: "artwork", label: "Artwork Master" },
    { id: "checklist", label: "Checklist" },
    { id: "machines", label: "Machines" },
    { id: "password", label: "Change Password" },
  ];
  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Configuration</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Admin</h1>
        <p className="text-slate-500 text-sm mt-2">Manage operators, customer master, default checklist and machine list.</p>
      </div>

      <div className="border-b border-slate-200 mb-6 flex gap-6 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`pb-3 text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${
              tab === t.id ? "text-slate-900 border-b-2 border-yellow-500" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersPanel />}
      {tab === "customers" && <CustomersPanel />}
      {tab === "artwork" && <ArtworkMasterPanel />}
      {tab === "checklist" && <ChecklistPanel />}
      {tab === "machines" && <MachinesPanel />}
      {tab === "password" && <ChangePasswordPanel />}
    </Layout>
  );
}

const PERMISSION_OPTIONS = [
  { key: "create_jobcard", label: "Create Job Card" },
  { key: "edit_jobcard", label: "Edit Job Card" },
  { key: "delete_jobcard", label: "Delete Job Card" },
  { key: "approve_artwork", label: "Approve Artwork" },
  { key: "manage_customers", label: "Manage Customers" },
  { key: "manage_checklist", label: "Manage Checklist" },
  { key: "manage_machines", label: "Manage Machines" },
  { key: "view_reports", label: "View Reports" },
];

function UsersPanel() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [resetUser, setResetUser] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operator", permissions: ["create_jobcard", "edit_jobcard"] });
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/users")).data));
  useEffect(() => { load(); }, []);
  const togglePerm = (k) => setForm((f) => ({
    ...f,
    permissions: f.permissions.includes(k) ? f.permissions.filter((p) => p !== k) : [...f.permissions, k],
  }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "operator", permissions: ["create_jobcard", "edit_jobcard"] });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/users/${confirmId}`);
      toast.success("User deleted");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDeleting(false); setConfirmId(null); }
  };
  const resetPassword = async (e) => {
    e.preventDefault();
    setResetting(true);
    try {
      await api.put(`/users/${resetUser.id}`, { password: resetPwd });
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
      setResetPwd("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setResetting(false); }
  };
  const adminCount = rows.filter((u) => u.role === "admin").length;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 border-slate-200 lg:col-span-2">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Users</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-2">Name</th><th className="py-2">Email</th><th className="py-2">Role</th><th className="py-2">Permissions</th><th></th></tr></thead>
          <tbody data-testid="users-tbody">
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 align-top">
                <td className="py-2">{u.name}</td>
                <td className="py-2 font-mono text-xs">{u.email}</td>
                <td className="py-2"><Badge variant="outline" className="text-[10px] uppercase">{u.role}</Badge></td>
                <td className="py-2">
                  {u.role === "admin" ? (
                    <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-medium">All</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(u.permissions || []).length === 0 && <span className="text-xs text-slate-400">None</span>}
                      {(u.permissions || []).map((p) => (
                        <span key={p} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{p}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" title="Reset password" onClick={() => { setResetUser(u); setResetPwd(""); }}><KeyRound className="w-4 h-4 text-slate-600" /></Button>
                  {u.id !== me?.id && !(u.role === "admin" && adminCount <= 1) && (
                    <Button variant="ghost" size="icon" title="Delete user" onClick={() => setConfirmId(u.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add user</div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email" /></Field>
          <Field label="Password"><PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-password" /></Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-white" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="operator">Operator</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </Field>
          {form.role !== "admin" && (
            <Field label="Permissions">
              <div className="space-y-1.5 border border-slate-200 rounded p-3 bg-slate-50">
                {PERMISSION_OPTIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(p.key)}
                      onChange={() => togglePerm(p.key)}
                      data-testid={`perm-${p.key}`}
                      className="rounded border-slate-300"
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </Field>
          )}
          {form.role === "admin" && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">Admins automatically get all permissions.</div>}
          <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="user-submit"><Plus className="w-4 h-4 mr-2" /> Create</Button>
        </form>
      </Card>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete User?"
        description="This will permanently delete the user account. This cannot be undone."
        confirmLabel="Delete User"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) setResetUser(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password for {resetUser?.name}</DialogTitle></DialogHeader>
          <form onSubmit={resetPassword} className="space-y-4">
            <Field label="New password"><PasswordInput value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} required minLength={6} autoFocus /></Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
              <Button type="submit" disabled={resetting} className="bg-slate-900 text-white hover:bg-slate-800">{resetting ? "Saving..." : "Reset password"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangePasswordPanel() {
  const empty = { current_password: "", new_password: "", confirm: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setForm(empty);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5 border-slate-200 max-w-md">
      <div className="text-lg font-semibold mb-1" style={{ fontFamily: "var(--font-heading)" }}>Change your password</div>
      <p className="text-sm text-slate-500 mb-4">Updates the password for the account you are logged in with.</p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Current password"><PasswordInput autoComplete="current-password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required /></Field>
        <Field label="New password"><PasswordInput autoComplete="new-password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required minLength={6} /></Field>
        <Field label="Confirm new password"><PasswordInput autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={6} /></Field>
        <Button type="submit" disabled={saving} className="w-full bg-slate-900 text-white hover:bg-slate-800"><KeyRound className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Update password"}</Button>
      </form>
    </Card>
  );
}

function CustomersPanel() {
  const [rows, setRows] = useState([]);
  const empty = { name: "", contact_person: "", email: "", phone: "", address: "", gst_no: "" };
  const [form, setForm] = useState(empty);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/customers")).data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      setForm(empty);
      toast.success("Customer added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${confirmId}`);
      toast.success("Customer deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 border-slate-200 lg:col-span-2">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Customer Master</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-2">Name</th><th className="py-2">Contact</th><th className="py-2">Email</th><th className="py-2">Phone</th><th className="py-2">GST</th><th></th></tr></thead>
            <tbody data-testid="customers-tbody">
              {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">No customers yet</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="py-2 font-medium">{c.name}</td>
                  <td className="py-2 text-slate-600">{c.contact_person || "—"}</td>
                  <td className="py-2 text-slate-600 text-xs">{c.email || "—"}</td>
                  <td className="py-2 text-slate-600 font-mono text-xs">{c.phone || "—"}</td>
                  <td className="py-2 text-slate-600 font-mono text-xs">{c.gst_no || "—"}</td>
                  <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => setConfirmId(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add customer</div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cust-name" /></Field>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="cust-contact" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" /></Field>
          <Field label="GST No."><Input value={form.gst_no} onChange={(e) => setForm({ ...form, gst_no: e.target.value })} data-testid="cust-gst" /></Field>
          <Field label="Address"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-address" /></Field>
          <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="cust-submit"><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </form>
      </Card>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Customer?"
        description="This will permanently delete the customer. This cannot be undone."
        confirmLabel="Delete Customer"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </div>
  );
}

function ChecklistPanel() {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/checklist-master")).data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    try {
      await api.post("/checklist-master", { label });
      setLabel("");
      toast.success("Checklist item added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/checklist-master/${confirmId}`);
      toast.success("Item deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 border-slate-200 lg:col-span-2">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Default Checklist Items</div>
        <p className="text-sm text-slate-500 mb-4">These items auto-populate on every new Job Card.</p>
        <div className="space-y-2" data-testid="checklist-tbody">
          {rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded">No checklist items yet.</div>}
          {rows.map((c) => (
            <div key={c.id} className="flex items-center gap-3 border border-slate-200 rounded p-3 bg-white">
              <div className="w-6 h-6 rounded bg-slate-100 grid place-items-center text-[10px] font-mono text-slate-600">{c.order + 1}</div>
              <span className="flex-1 text-sm">{c.label}</span>
              <Button variant="ghost" size="icon" onClick={() => setConfirmId(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add item</div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Label"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. QA sample approved" required data-testid="checklist-label" /></Field>
          <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="checklist-submit"><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </form>
      </Card>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Checklist Item?"
        description="This will permanently delete the checklist item. This cannot be undone."
        confirmLabel="Delete Item"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </div>
  );
}

function MachinesPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", code: "" });
  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/machines")).data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/machines", form);
      setForm({ name: "", code: "" });
      toast.success("Machine added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/machines/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 border-slate-200 lg:col-span-2">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Machines</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="py-2">Name</th><th className="py-2">Code</th><th></th></tr></thead>
          <tbody data-testid="machines-tbody">
            {rows.map((m) => (
              <tr key={m.id} className="border-b border-slate-100">
                <td className="py-2">{m.name}</td>
                <td className="py-2 font-mono text-xs">{m.code}</td>
                <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => remove(m.id)}><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add machine</div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="machine-name" /></Field>
          <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="machine-code" /></Field>
          <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="machine-submit"><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </form>
      </Card>
    </div>
  );
}

function ArtworkMasterPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", version: "", customer_name: "" });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/artwork-master")).data));
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/artwork", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedFile(data);
      toast.success(`Uploaded ${data.original_filename}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Artwork name is required"); return; }
    try {
      await api.post("/artwork-master", {
        ...form,
        file_id: uploadedFile?.id || null,
        storage_path: uploadedFile?.storage_path || "",
        original_filename: uploadedFile?.original_filename || "",
        size: uploadedFile?.size || 0,
        content_type: uploadedFile?.content_type || "",
      });
      setForm({ name: "", description: "", version: "", customer_name: "" });
      setUploadedFile(null);
      toast.success("Artwork saved to master");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/artwork-master/${confirmId}`);
      toast.success("Artwork deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-5 border-slate-200 lg:col-span-2">
        <div className="text-lg font-semibold mb-1" style={{ fontFamily: "var(--font-heading)" }}>Artwork Master</div>
        <p className="text-sm text-slate-500 mb-4">Saved artworks can be picked directly when creating a job card.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Version</th>
                <th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">File</th>
                <th className="py-2 pr-3">Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-500">No artworks saved yet.</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 align-top">
                  <td className="py-2 pr-3 font-medium">{a.name}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{a.version || "—"}</td>
                  <td className="py-2 pr-3 text-slate-600">{a.customer_name || "—"}</td>
                  <td className="py-2 pr-3">
                    {a.file_id ? (
                      <a href={`${API_BASE.replace("/api", "")}/uploads/${a.storage_path}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900">
                        <FileText className="w-3 h-3" /> {a.original_filename}
                      </a>
                    ) : <span className="text-slate-400 text-xs">No file</span>}
                  </td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{a.description || "—"}</td>
                  <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => setConfirmId(a.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5 border-slate-200">
        <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add Artwork</div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Artwork Name *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Nike Label v2" required />
          </Field>
          <Field label="Version">
            <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="e.g. v1.2" />
          </Field>
          <Field label="Customer">
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Which customer is this for" />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes about this artwork" />
          </Field>
          <Field label="Artwork File">
            <input ref={fileRef} type="file" onChange={handleFile} className="hidden" accept="application/pdf,image/*" />
            {uploadedFile ? (
              <div className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50">
                <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                <span className="text-sm truncate flex-1">{uploadedFile.original_filename}</span>
                <button type="button" onClick={() => setUploadedFile(null)} className="text-xs text-slate-500 hover:text-red-600">✕</button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="w-full" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Upload File"}
              </Button>
            )}
          </Field>
          <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> Save to Master
          </Button>
        </form>
      </Card>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Artwork?"
        description="This will permanently delete the artwork from master. This cannot be undone."
        confirmLabel="Delete Artwork"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">{label}</Label>
      {children}
    </div>
  );
}
